import { useActions, useReduxState } from "re-reduced";
import React, { useCallback } from "react";
import styled from "styled-components";

import * as selectors from "@state/selectors";
import { DeviceTicket, RequestStatus } from "@state/types";
import { Button, Loading, Modal } from "@sub";
import { colorsRGB, getStatusColor } from "@util/constants";
import { ticketHolderName, TIMEOUT_MSG } from "@util/helper";
import { ticketHolderEmail } from "@util/ticketDetails";
import { Container, P } from "@util/standard";
import { SCAN_STATUS } from "@util/types";
import { TicketScanValidations } from "@graphql/sdk";
import actions from "@state/actions";
import TicketDetails from "./ticketDetails";

const ModalContainer = styled(Container)`
  flex-direction: column;
  gap: 16px;
  min-height: 100%;
  overflow-wrap: anywhere;
`;

const Title = styled(P)`
  color: ${colorsRGB.white(0.7)};
  margin: 0 0 4px;
  font-size: 12px;
  line-height: 1.4;
`;

const TicketBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SupportingDetails = styled.div`
  display: grid;
  gap: 12px;
`;

const Divider = styled.hr`
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid ${colorsRGB.white(0.15)};
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
  padding-top: 8px;
`;

const statusText = {
  success: "Valid",
  error: "Invalid",
  alreadyScanned: "Already scanned",
  idle: "",
  issue: "Issue",
  wrongGate: "Wrong gate",
  timeWarning: "Wrong entry time", // TODO: Suspect the wording here is wrong, (or the intention for this to be an error & display the error texrt?)
};

interface Props {
  status: SCAN_STATUS;
  errorMsg: string | undefined;
  ticket: DeviceTicket | undefined;
  handleModalClose: () => void;
  modalVisible: boolean;
  entryTimeMessage?: string;
}

const propOrDefault = (prop: string | undefined, _default: string = "-") =>
  prop ? prop : _default;

const ResponseModal = (props: Props) => {
  const { ticket, errorMsg, status, handleModalClose, modalVisible } = props;

  const statusColor = getStatusColor(status!);
  const bypassColor = getStatusColor("default" as SCAN_STATUS);

  const name = ticketHolderName(ticket);
  const email = ticketHolderEmail(ticket);
  const orderNumber = ticket?.orderNumber as string;

  const { scanTicket } = useActions(actions);
  const handleAdmitAnyway = useCallback(() => {
    if (ticket?.ticketNumber) {
      scanTicket({
        ticketNumber: ticket.ticketNumber,
        bypassValidations: [TicketScanValidations.EntryTime],
      });
    }
  }, [scanTicket, ticket?.ticketNumber]);

  const [primaryErrorMessage, ...secondaryErrorMessages] = errorMsg
    ? errorMsg.split("\n")
    : [];

  const { requestStatus } = useReduxState({
    requestStatus: selectors.getRequestStatus,
  });

  const Info = ({
    title,
    info,
  }: {
    title: string;
    info: string | undefined;
  }) => (
    <Container flexDirection="column">
      <Title fontSize={12}>{title.toUpperCase()}</Title>
      <P noMargin fontSize={16}>
        {propOrDefault(info)}
      </P>
    </Container>
  );

  return (
    <Modal
      visible={modalVisible}
      onClose={handleModalClose}
      maskBackgroundColor={status === "idle" ? "black" : statusColor}
    >
      <ModalContainer>
        {status && (
          <P
            margin="0 36px 0 0"
            color={statusColor}
            fontSize={30}
            fontFamily="bioSansBold"
          >{`${statusText[status].toUpperCase()}`}</P>
        )}
        {primaryErrorMessage && (
          <div role="alert">
            <P
              color={statusColor}
              fontSize={20}
              fontFamily="bioSansBold"
              margin="10px 0 0 0"
            >{`${primaryErrorMessage}`}</P>

            {secondaryErrorMessages && secondaryErrorMessages.length > 0 && (
              <ul>
                {secondaryErrorMessages.map((msg, index) => (
                  <li key={index}>{msg}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {ticket ? (
          <TicketBody>
            {ticket.event?.title && (
              <P noMargin fontSize={14} opacity={0.7}>
                {ticket.event.title}
              </P>
            )}
            <TicketDetails ticket={ticket} />
            {(name || email) && (
              <>
                <Divider />
                <div>
                  <Title>TICKET HOLDER DETAILS</Title>
                  {name && (
                    <P noMargin fontSize={22}>
                      {name}
                    </P>
                  )}
                  {email && (
                    <P margin="4px 0 0" fontSize={16}>
                      {email}
                    </P>
                  )}
                </div>
              </>
            )}
            <SupportingDetails>
              <Info title="Order number" info={orderNumber} />
              <Info title="QR code" info={ticket.ticketNumber} />
            </SupportingDetails>
          </TicketBody>
        ) : (
          <Container
            width="100%"
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            {(!errorMsg || errorMsg === TIMEOUT_MSG) && <Loading />}
          </Container>
        )}
        <ModalActions>
          {status === "timeWarning" && (
            <Button
              theme="inverse"
              text="Admit anyway"
              onClick={handleAdmitAnyway}
              color={bypassColor}
              borderColor={bypassColor}
            />
          )}
          {requestStatus !== RequestStatus.Pending && (
            <Button
              theme="inverse"
              text={status == "success" ? "Continue" : "Close warning"}
              onClick={handleModalClose}
              color={statusColor}
              borderColor={statusColor}
            />
          )}
        </ModalActions>
      </ModalContainer>
    </Modal>
  );
};

export default ResponseModal;
